"use client";

import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Button from "@/components/library/Button";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  country: string;
  jobTitle: string;
  orgType: string;
  teamSize: string;
  referralSource: string;
  productInterest: string;
}

const ContactSalesForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    country: "",
    jobTitle: "",
    orgType: "",
    teamSize: "",
    referralSource: "",
    productInterest: "",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // @ts-expect-error - ChiliPiper is dynamically loaded at runtime and not available in TypeScript types
    if (window.ChiliPiper) {
      // @ts-expect-error - ChiliPiper API is not typed in our project but exists at runtime
      window.ChiliPiper.submit("lula-is", "gail", {
        trigger: "InAppButton",
        lead: {
          name: formData.firstName,
          lastname: formData.lastName,
          email: formData.email,
          companyname: formData.company,
          business_line__c: formData.jobTitle,
          leadsource: formData.referralSource,
          leadsourcedetail: formData.referralSource,
          productinterest: formData.productInterest,
        },
      });
    }
  };

  useEffect(() => {
    // Load ChiliPiper script
    const script = document.createElement("script");
    script.id = "chilipiper-concierge";
    script.src = "https://lula-is.chilipiper.com/concierge-js/cjs/concierge.js";
    script.crossOrigin = "anonymous";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Clean up script when component unmounts
      if (document.getElementById("chilipiper-concierge")) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="bg-white/80 backdrop-blur-xs rounded-2xl shadow-xl p-8">
      <form className="space-y-8" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium mb-1"
            >
              {t("requestDemo.form.firstName")}{" "}
              <span className="text-black">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder={t("requestDemo.form.placeholders.firstName")}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            />
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium mb-1"
            >
              {t("requestDemo.form.lastName")}{" "}
              <span className="text-black">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder={t("requestDemo.form.placeholders.lastName")}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              {t("requestDemo.form.email")}{" "}
              <span className="text-black">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t("requestDemo.form.placeholders.email")}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            />
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium mb-1">
              {t("requestDemo.form.company")}{" "}
              <span className="text-black">*</span>
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder={t("requestDemo.form.placeholders.company")}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            />
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium mb-1">
              {t("requestDemo.form.country")}{" "}
              <span className="text-black">*</span>
            </label>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            >
              <option value="">{t("requestDemo.form.select.default")}</option>
              <option value="us">
                {t("requestDemo.form.select.countries.us")}
              </option>
              <option value="ca">
                {t("requestDemo.form.select.countries.ca")}
              </option>
              <option value="uk">
                {t("requestDemo.form.select.countries.uk")}
              </option>
              <option value="other">
                {t("requestDemo.form.select.countries.other")}
              </option>
            </select>
          </div>

          <div>
            <label
              htmlFor="jobTitle"
              className="block text-sm font-medium mb-1"
            >
              {t("requestDemo.form.jobTitle")}{" "}
              <span className="text-black">*</span>
            </label>
            <input
              type="text"
              id="jobTitle"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              placeholder={t("requestDemo.form.placeholders.jobTitle")}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            />
          </div>

          <div>
            <label htmlFor="orgType" className="block text-sm font-medium mb-1">
              {t("requestDemo.form.orgType")}{" "}
              <span className="text-black">*</span>
            </label>
            <select
              id="orgType"
              name="orgType"
              value={formData.orgType}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            >
              <option value="">{t("requestDemo.form.select.default")}</option>
              <option value="insuranceCompany">
                {t("requestDemo.form.select.orgTypes.insuranceCompany")}
              </option>
              <option value="insuranceAgency">
                {t("requestDemo.form.select.orgTypes.insuranceAgency")}
              </option>
              <option value="bank">
                {t("requestDemo.form.select.orgTypes.bank")}
              </option>
              <option value="mortgage">
                {t("requestDemo.form.select.orgTypes.mortgage")}
              </option>
              <option value="wealthManagement">
                {t("requestDemo.form.select.orgTypes.wealthManagement")}
              </option>
              <option value="investmentFirm">
                {t("requestDemo.form.select.orgTypes.investmentFirm")}
              </option>
              <option value="fintech">
                {t("requestDemo.form.select.orgTypes.fintech")}
              </option>
              <option value="consulting">
                {t("requestDemo.form.select.orgTypes.consulting")}
              </option>
              <option value="legal">
                {t("requestDemo.form.select.orgTypes.legal")}
              </option>
              <option value="finance">
                {t("requestDemo.form.select.orgTypes.finance")}
              </option>
              <option value="other">
                {t("requestDemo.form.select.orgTypes.other")}
              </option>
            </select>
          </div>

          <div>
            <label
              htmlFor="teamSize"
              className="block text-sm font-medium mb-1"
            >
              {t("requestDemo.form.teamSize")}{" "}
              <span className="text-black">*</span>
            </label>
            <input
              type="number"
              id="teamSize"
              name="teamSize"
              value={formData.teamSize}
              onChange={handleChange}
              required
              placeholder={t("requestDemo.form.placeholders.teamSize")}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            />
          </div>

          <div>
            <label
              htmlFor="productInterest"
              className="block text-sm font-medium mb-1"
            >
              {t("requestDemo.form.productInterest")}{" "}
              <span className="text-black">*</span>
            </label>
            <select
              id="productInterest"
              name="productInterest"
              value={formData.productInterest}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            >
              <option value="">{t("requestDemo.form.select.default")}</option>
              <option value="gailgpt">
                {t("requestDemo.form.productOptions.gailgpt")}
              </option>
              <option value="gailagent">
                {t("requestDemo.form.productOptions.gailagent")}
              </option>
              <option value="both">
                {t("requestDemo.form.productOptions.both")}
              </option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label
              htmlFor="referralSource"
              className="block text-sm font-medium mb-1"
            >
              {t("requestDemo.form.referralSource")}{" "}
              <span className="text-black">*</span>
            </label>
            <textarea
              id="referralSource"
              name="referralSource"
              value={formData.referralSource}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-800"
            />
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-500 mt-2 text-left max-w-lg">
            <Trans i18nKey="contactPrivacy">
              <div>
                By submitting this form, you agree to receive recurring
                automated product updates and operational update texts from Gail
                to the number entered. Consent isn&apos;t a condition of any
                service. Reply HELP for help or STOP to cancel.
              </div>
              <div className="mt-1">
                Message frequency may vary. Msg & data rates may apply. View our{" "}
              </div>
              <a
                href="/terms-condition"
                className={clsx("underline", "text-primary")}
              >
                Terms
              </a>
              &amp;
              <a
                href="/privacy-policy"
                className={clsx("underline", "text-primary")}
              >
                Privacy
              </a>
              .
            </Trans>
          </p>
          <div className="flex justify-end">
            <Button type="primary" onClick={handleSubmit}>
              {t("requestDemo.submit")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactSalesForm;
